import { Link } from 'react-router-dom';

import CriteriaList from './criteria-list';
import RoundHistory from './round-history';
import Button from '../../../common/components/button/button';
import PageHeader from '../../../common/components/page-header/page-header';
import RelativeTime from '../../../common/components/relative-time/relative-time';
import Section from '../../../common/components/section/section';
import StatusPill from '../../../common/components/status-pill/status-pill';
import EmptyState from '../../../common/components/state-message/empty-state';
import ErrorState from '../../../common/components/state-message/error-state';
import LoadingState from '../../../common/components/state-message/loading-state';
import Tag from '../../../common/components/tag/tag';
import Timeline from '../../../common/components/timeline/timeline';
import ShareIcon from '../../../common/icons/share';
import {
    ESTIMATE_LABEL,
    EVENT_KIND_TONE,
    REPOSITORY_KIND_LABEL,
    TASK_KIND_LABEL,
    WORKFLOW_STATE_LABEL,
    WORKFLOW_STATE_TONE,
} from '../../../core/constants/domain';
import { ROUTES } from '../../../core/constants/routes';
import Gate from '../../../global/contexts/auth/gate';
import {
    nameOf,
    useProjectCatalog,
    useRepositoryCatalog,
    useTierCatalog,
} from '../../../global/contexts/catalogs/catalogs';
import { useEvents, useTask, useTaskReviewRounds } from '../../../global/contexts/work/work';
import { useShareTask } from '../hooks/useShareTask';

import '../styles/detail.css';

export default function TaskDetail({ taskId }) {
    const taskQuery = useTask(taskId);
    const roundsQuery = useTaskReviewRounds(taskId);
    const eventsQuery = useEvents({ taskId, limit: 20 });
    const { data: projects } = useProjectCatalog();
    const { data: tiers } = useTierCatalog();
    const { data: repositories } = useRepositoryCatalog();
    const { share, isSharing } = useShareTask();

    if (taskQuery.isPending) return <LoadingState label="Cargando tarea" rows={5} />;
    if (taskQuery.isError) {
        return (
            <>
                <PageHeader backTo={ROUTES.WORK} backLabel="Trabajo" title={`Tarea #${taskId}`} />
                <ErrorState error={taskQuery.error} title="No pudimos abrir esta tarea" onRetry={taskQuery.refetch} />
            </>
        );
    }

    const task = taskQuery.data;
    const rounds = roundsQuery.data?.items ?? [];
    const latestRound = rounds[0] ?? null;
    const openRound = rounds.find((round) => round.isOpen) ?? null;
    const repository = repositories?.get(task.repositoryId);
    const trail = (eventsQuery.data?.items ?? []).map((event) => ({
        id: event.id,
        at: event.at,
        label: event.kindLabel,
        title: event.summary,
        actor: event.actor,
        tone: EVENT_KIND_TONE[event.kind],
    }));

    return (
        <div className="task-detail">
            <PageHeader
                backTo={ROUTES.WORK}
                backLabel="Trabajo"
                eyebrow={`#${task.id} · ${nameOf(projects, task.projectId, 'Proyecto')}`}
                title={task.title}
                actions={(
                    <Button icon={<ShareIcon size={18} />} variant="secondary" size="small" isBusy={isSharing} onClick={() => share(task)}>
                        Compartir
                    </Button>
                )}
                meta={(
                    <>
                        <StatusPill tone={WORKFLOW_STATE_TONE[task.state]}>{WORKFLOW_STATE_LABEL[task.state] ?? task.state}</StatusPill>
                        <Tag>{TASK_KIND_LABEL[task.kind] ?? task.kind}</Tag>
                        {task.estimate && <Tag>{ESTIMATE_LABEL[task.estimate] ?? task.estimate}</Tag>}
                        {task.modality === 'one_off' && <Tag swatch="accent">Puntual</Tag>}
                    </>
                )}
            />

            {task.state === 'blocked' && task.blockedReason && (
                <p className="task-detail__callout task-detail__callout--blocked">
                    <strong>Bloqueada:</strong> {task.blockedReason}
                </p>
            )}
            {task.state === 'returned' && task.lastReturn && (
                <p className="task-detail__callout task-detail__callout--returned">
                    <strong>Devuelta <RelativeTime value={task.lastReturn.at} />:</strong> {task.lastReturn.reason}
                </p>
            )}
            {openRound && (
                <Gate domain="tasks:review">
                    <div className="task-detail__callout task-detail__callout--review">
                        <span>La ronda {openRound.number} espera una decisión de QA.</span>
                        <Button variant="primary" size="small" to={ROUTES.REVIEW(task.id)}>Revisar</Button>
                    </div>
                </Gate>
            )}

            <div className="task-detail__columns">
                <div className="task-detail__main">
                    {task.description && <p className="task-detail__description">{task.description}</p>}

                    <Section title="Criterios de aceptación" count={task.criteria.length}>
                        {task.criteria.length === 0
                            ? <EmptyState title="Sin criterios" description="Una tarea sin criterios no puede revisarse." />
                            : <CriteriaList criteria={task.criteria} findings={latestRound?.findings} />}
                        {latestRound && (
                            <p className="task-detail__hint">Resultados de la ronda {latestRound.number}.</p>
                        )}
                    </Section>

                    <Section title="Revisión" count={roundsQuery.isSuccess ? rounds.length : undefined}>
                        {roundsQuery.isPending && <LoadingState label="Cargando rondas" rows={2} />}
                        {roundsQuery.isError && <ErrorState error={roundsQuery.error} onRetry={roundsQuery.refetch} />}
                        {roundsQuery.isSuccess && rounds.length === 0 && (
                            <EmptyState title="Todavía no hay rondas" description="La primera ronda se abre cuando un agente entrega." />
                        )}
                        {rounds.length > 0 && <RoundHistory rounds={rounds} />}
                    </Section>
                </div>

                <aside className="task-detail__aside">
                    <Section title="Entrega">
                        <dl className="task-detail__facts">
                            <div><dt>Tier</dt><dd>{nameOf(tiers, task.tierId, 'Tier')}</dd></div>
                            <div>
                                <dt>Repositorio</dt>
                                <dd>
                                    <span className="mono">{repository?.name ?? `#${task.repositoryId}`}</span>
                                    {repository?.kind && <Tag swatch={repository.kind}>{REPOSITORY_KIND_LABEL[repository.kind]}</Tag>}
                                </dd>
                            </div>
                            <div><dt>Responsable</dt><dd>{task.assignee ?? 'Sin responsable'}</dd></div>
                            <div><dt>Reservada por</dt><dd>{task.claimedBy ?? 'Nadie'}</dd></div>
                            <div><dt>Revisión de spec</dt><dd className="mono">{task.specRevision ?? '—'}</dd></div>
                            <div><dt>Actualizada</dt><dd><RelativeTime value={task.updatedAt} /></dd></div>
                        </dl>
                        {task.scope.length > 0 && (
                            <>
                                <h3 className="task-detail__subheading">Alcance de archivos</h3>
                                <ul className="task-detail__scope">
                                    {task.scope.map((path) => <li key={path} className="mono">{path}</li>)}
                                </ul>
                            </>
                        )}
                        <h3 className="task-detail__subheading">Commits</h3>
                        {task.commits.length === 0
                            ? <p className="task-detail__hint">Sin commits vinculados.</p>
                            : (
                                <ul className="task-detail__commits">
                                    {task.commits.map((commit) => (
                                        <li key={commit.hash} className="task-detail__commit">
                                            <span className="mono task-detail__hash">{commit.hash}</span>
                                            <span>{commit.message}</span>
                                            <RelativeTime value={commit.at} className="task-detail__commit-time" />
                                        </li>
                                    ))}
                                </ul>
                            )}
                    </Section>

                    <Section title="Trazabilidad">
                        {task.originTaskId && (
                            <p className="task-detail__hint">
                                Hallazgo surgido de <Link className="task-detail__link" to={ROUTES.TASK(task.originTaskId)}>la tarea #{task.originTaskId}</Link>.
                            </p>
                        )}
                        {eventsQuery.isPending && <LoadingState label="Cargando eventos" rows={2} />}
                        {eventsQuery.isError && <ErrorState error={eventsQuery.error} onRetry={eventsQuery.refetch} />}
                        {eventsQuery.isSuccess && trail.length === 0 && <EmptyState title="Sin eventos registrados" />}
                        {trail.length > 0 && <Timeline items={trail} label="Eventos de la tarea" />}
                    </Section>
                </aside>
            </div>
        </div>
    );
}
